import React, { useState, useEffect } from 'react';
import { useAuth } from '../../firebase/AuthContext';
import { updateUserProfile } from '../../firebase/firestoreService';
import styled from 'styled-components';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { FaCreditCard, FaTrash, FaCheckCircle } from 'react-icons/fa';

// Initialize Stripe with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const StripeContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const Card = styled.div`
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  color: #333;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eee;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #555;
`;

const Button = styled.button`
  background-color: #4285f4;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  align-self: flex-start;
  
  &:hover {
    background-color: #3367d6;
  }
  
  &:disabled {
    background-color: #b3b3b3;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #d93025;
  margin-top: 0.5rem;
  font-size: 0.875rem;
`;

const SuccessMessage = styled.div`
  color: #0f9d58;
  margin-top: 0.5rem;
  font-size: 0.875rem;
`;

const CardElementContainer = styled.div`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 1rem;
  background-color: #f9f9f9;
`;

const PaymentMethodList = styled.div`
  margin-top: 2rem;
`;

const PaymentMethod = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border: 1px solid #eee;
  border-radius: 4px;
  margin-bottom: 1rem;
  background-color: #f9f9f9;
`;

const CardInfo = styled.div`
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 1rem;
    font-size: 1.5rem;
    color: #333;
  }
`;

const CardDetails = styled.div`
  span {
    display: block;
    
    &:first-child {
      font-weight: 500;
    }
    
    &:last-child {
      color: #666;
      font-size: 0.875rem;
    }
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.delete ? '#d93025' : '#4285f4'};
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

// Card input style options
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

// Payment form component
const PaymentForm = ({ onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Create a payment method
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
        billing_details: {
          email: currentUser.email,
          name: currentUser.displayName || currentUser.email,
        },
      });
      
      if (stripeError) {
        throw new Error(stripeError.message);
      }
      
      // Check if customer exists in Stripe or create one
      let customerId = '';
      
      // Get customer ID from Firestore or create a new one
      const userProfileResponse = await fetch('https://us-central1-master-collectors-forge.cloudfunctions.net/api/create-customer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: currentUser.email,
          name: currentUser.displayName || currentUser.email,
          uid: currentUser.uid,
        }),
      });
      
      const customerData = await userProfileResponse.json();
      customerId = customerData.customerId;
      
      // Attach payment method to customer
      await fetch('https://us-central1-master-collectors-forge.cloudfunctions.net/api/add-payment-method', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentMethodId: paymentMethod.id,
          customerId,
        }),
      });
      
      // Update user profile in Firestore
      await updateUserProfile(currentUser.uid, {
        stripeCustomerId: customerId,
        hasPaymentMethod: true,
        lastUpdated: new Date()
      });
      
      // Clear the form
      elements.getElement(CardElement).clear();
      
      // Notify parent component
      onSuccess();
    } catch (error) {
      console.error('Error adding payment method:', error);
      setError(error.message || 'Failed to add payment method. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Form onSubmit={handleSubmit}>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      <FormGroup>
        <Label>Card Details</Label>
        <CardElementContainer>
          <CardElement options={cardElementOptions} />
        </CardElementContainer>
      </FormGroup>
      
      <Button type="submit" disabled={!stripe || loading}>
        {loading ? 'Processing...' : 'Add Payment Method'}
      </Button>
    </Form>
  );
};

// Main Stripe Integration component
const StripeIntegration = () => {
  const { currentUser } = useAuth();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [customerId, setCustomerId] = useState('');
  
  // Fetch customer ID and payment methods
  const fetchCustomerData = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      // First, try to get the customer ID from Firestore
      // If not available, create a new customer
      let customerIdFromFirestore = '';
      
      // Create a customer if one doesn't exist
      const createCustomerResponse = await fetch('https://us-central1-master-collectors-forge.cloudfunctions.net/api/create-customer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: currentUser.email,
          name: currentUser.displayName || currentUser.email,
          uid: currentUser.uid,
        }),
      });
      
      const customerData = await createCustomerResponse.json();
      customerIdFromFirestore = customerData.customerId;
      
      // Update state with customer ID
      setCustomerId(customerIdFromFirestore);
      
      // Update Firestore with customer ID if needed
      await updateUserProfile(currentUser.uid, {
        stripeCustomerId: customerIdFromFirestore,
      });
      
      // Fetch payment methods
      await fetchPaymentMethods(customerIdFromFirestore);
    } catch (error) {
      console.error('Error fetching customer data:', error);
      setError('Failed to load customer data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch payment methods from Stripe
  const fetchPaymentMethods = async (stripeCustomerId) => {
    if (!stripeCustomerId) return;
    
    try {
      const response = await fetch(`https://us-central1-master-collectors-forge.cloudfunctions.net/api/payment-methods/${stripeCustomerId}`);
      const data = await response.json();
      
      if (Array.isArray(data)) {
        setPaymentMethods(data);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      setError('Failed to load payment methods');
    }
  };
  
  // Handle successful payment method addition
  const handlePaymentMethodSuccess = () => {
    setSuccess('Payment method added successfully!');
    fetchPaymentMethods(customerId);
  };
  
  // Set default payment method
  const handleSetDefault = async (paymentMethodId) => {
    setLoading(true);
    
    try {
      await fetch('https://us-central1-master-collectors-forge.cloudfunctions.net/api/set-default-payment-method', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId,
          paymentMethodId,
        }),
      });
      
      // Update local state
      const updatedPaymentMethods = paymentMethods.map(method => ({
        ...method,
        isDefault: method.id === paymentMethodId
      }));
      
      setPaymentMethods(updatedPaymentMethods);
      setSuccess('Default payment method updated');
    } catch (error) {
      console.error('Error setting default payment method:', error);
      setError('Failed to update default payment method');
    } finally {
      setLoading(false);
    }
  };
  
  // Delete payment method
  const handleDelete = async (paymentMethodId) => {
    setLoading(true);
    
    try {
      await fetch('https://us-central1-master-collectors-forge.cloudfunctions.net/api/delete-payment-method', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentMethodId,
        }),
      });
      
      // Update local state
      const updatedPaymentMethods = paymentMethods.filter(
        method => method.id !== paymentMethodId
      );
      
      setPaymentMethods(updatedPaymentMethods);
      setSuccess('Payment method removed');
    } catch (error) {
      console.error('Error removing payment method:', error);
      setError('Failed to remove payment method');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (currentUser) {
      fetchCustomerData();
    }
  }, [currentUser]);
  
  return (
    <Elements stripe={stripePromise}>
      <StripeContainer>
        <Card>
          <Title>Payment Methods</Title>
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}
          
          <PaymentForm onSuccess={handlePaymentMethodSuccess} />
          
          {paymentMethods.length > 0 && (
            <PaymentMethodList>
              <h3>Saved Payment Methods</h3>
              
              {paymentMethods.map(method => (
                <PaymentMethod key={method.id}>
                  <CardInfo>
                    <FaCreditCard />
                    <CardDetails>
                      <span>{method.card.brand.toUpperCase()} •••• {method.card.last4}</span>
                      <span>Expires {method.card.exp_month}/{method.card.exp_year}</span>
                      {method.isDefault && (
                        <span style={{ color: '#0f9d58', fontWeight: 500 }}>
                          <FaCheckCircle style={{ marginRight: '5px' }} />
                          Default
                        </span>
                      )}
                    </CardDetails>
                  </CardInfo>
                  
                  <div>
                    {!method.isDefault && (
                      <ActionButton onClick={() => handleSetDefault(method.id)}>
                        Set as Default
                      </ActionButton>
                    )}
                    <ActionButton delete onClick={() => handleDelete(method.id)}>
                      <FaTrash style={{ marginRight: '5px' }} />
                      Remove
                    </ActionButton>
                  </div>
                </PaymentMethod>
              ))}
            </PaymentMethodList>
          )}
        </Card>
        
        <Card>
          <Title>Implementation Notes</Title>
          <p>
            This is a real Stripe integration. To make it fully functional:
          </p>
          <ol>
            <li>Replace the placeholder Stripe API keys in your .env file with your actual Stripe API keys</li>
            <li>Start the backend server with <code>node server/server.js</code></li>
            <li>Make sure your Firebase security rules allow storing the Stripe customer ID</li>
            <li>For production, deploy your backend to a secure server</li>
          </ol>
          <p>
            For more information, refer to the{' '}
            <a 
              href="https://stripe.com/docs/payments/accept-a-payment?platform=web&ui=elements" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              Stripe documentation
            </a>
            .
          </p>
        </Card>
      </StripeContainer>
    </Elements>
  );
};

export default StripeIntegration;
